import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { Fragment, type SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import { CartNotification } from '~/components/Header/components/CartNotification';
import Icon from '~/components/Icon/Icon';
import type { IconTypes } from '~/components/Icon/IconTypes';
import { ModalType } from '~/components/Modal/interface';
import { REDIRECT_QUERY_NAME } from '~/components/ModalDeliveryAddress/constants';
import { UI_BOTTOM_COMPONENTS } from '~/constants/variables';
import { useModal } from '~/hooks/useModal';
import { BottomNavigationType } from '~/pages/Root/BottomNavigationBar/interfaces';
import { cartStore, userStore } from '~/stores';
import { getAddressModalType } from '~/utils/addressModal';

import { ROUTES } from '../constants';

import styles from './BottomNavigationBar.module.scss';
import { BOTTOM_NAVIGATION_ITEMS } from './constants';

interface BottomNavigationBarIconType {
  type: IconTypes;
  title: string;
  link: string;
  className?: string;
  isActive?: boolean;
  colorPassive: string;
  colorActivePrimary: string;
  colorActiveSecondary: string;
}

const BottomNavigationBarIcon = observer(
  ({
    type,
    title,
    link,
    className,
    isActive,
    colorPassive,
    colorActivePrimary,
    colorActiveSecondary,
  }: BottomNavigationBarIconType) => {
    const { openModal } = useModal();
    const { t } = useTranslation();

    const isCabinetLink = link === '/cabinet';

    const handleClick = (event: SyntheticEvent) => {
      if (isCabinetLink) {
        if (!userStore.isAuthorized) {
          openModal(ModalType.Auth, 'footer', { redirect: '/cabinet' });
          event.preventDefault();
        }
      }
      if (link === '/cart') {
        if (
          company.hasAgeRestriction &&
          userStore.isAuthorized &&
          !userStore.personalData.dateOfBirth &&
          cartStore.hasRestrictedCategoriesInCart
        ) {
          openModal(ModalType.BirthDate, 'footer', {
            [REDIRECT_QUERY_NAME]: '/cart',
          });
          event.preventDefault();
        }

        if (!userStore.deliveryAddress) {
          const targetModal = getAddressModalType();

          openModal(targetModal, 'footer', {
            [REDIRECT_QUERY_NAME]: '/cart',
          });
          event.preventDefault();
        }
      }
    };

    return (
      <Link
        data-link={`mobile-navigator-${type}`}
        className={classes([styles.icon, className])}
        to={link}
        onClick={handleClick}
      >
        {company.match({
          citydrinks: link === ROUTES.cart && (
            <CartNotification classNames={styles.cartNotificationCustom} />
          ),
          default: null,
        })}
        <Icon
          type={type}
          size={24}
          styleVars={{
            '--colorPrimary': isActive ? colorActivePrimary : colorPassive,
            '--colorSecondary': isActive ? colorActiveSecondary : 'transparent',
          }}
        />
        <div className={styles.title}>
          {isCabinetLink && !userStore.isAuthorized ? t('logIn') : title}
        </div>
      </Link>
    );
  },
);

const BottomNavigationBar = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const icons = BOTTOM_NAVIGATION_ITEMS[company.name];

  const [colorPassive, setColorPassive] = useState('');
  const [colorActivePrimary, setColorActivePrimary] = useState('');
  const [colorActiveSecondary, setColorActiveSecondary] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const root = window.getComputedStyle(document.documentElement);

      if (root) {
        setColorPassive(root.getPropertyValue('--text400Sec').trim());
        setColorActivePrimary(root.getPropertyValue('--brandAtom900').trim());
        setColorActiveSecondary(
          root.getPropertyValue('--navBarIconFill').trim(),
        );
      }
    }
  }, []);

  return (
    <div
      className={styles.container}
      data-company={company.name}
      id={UI_BOTTOM_COMPONENTS.NAVBAR}
    >
      {icons.map((navItem) => {
        if (navItem.type === BottomNavigationType.LINK) {
          const { key, link, title, iconType } = navItem;
          const iconName =
            CompanyName.CityDrinks === company.name && pathname === link
              ? (`${iconType}-fill` as IconTypes)
              : iconType;
          const isActive = pathname === link;

          return link === '/cart' ? (
            <div className={styles.iconContainer} key={title}>
              <BottomNavigationBarIcon
                type={iconName}
                className={classes(isActive && styles.selected)}
                title={t(title)}
                link={link}
                key={key}
                isActive={isActive}
                colorPassive={colorPassive}
                colorActivePrimary={colorActivePrimary}
                colorActiveSecondary={colorActiveSecondary}
              />
              {cartStore.totalCartCount > 0 && (
                <div className={styles.cartCounter}>
                  {cartStore.totalCartCount}
                </div>
              )}
            </div>
          ) : (
            <BottomNavigationBarIcon
              type={iconName}
              className={classes(isActive && styles.selected)}
              title={t(title)}
              link={link}
              key={key}
              isActive={isActive}
              colorPassive={colorPassive}
              colorActivePrimary={colorActivePrimary}
              colorActiveSecondary={colorActiveSecondary}
            />
          );
        }

        const { node, key } = navItem;
        return <Fragment key={key}>{node}</Fragment>;
      })}
    </div>
  );
};

export default observer(BottomNavigationBar);
